import {
  useSearchUsersQuery,
  useSearchChartLayoutQuery,
  useSearchIHEquipmentQuery,
  useSearchWorkflowQuery,
  useSearchFlagQuery,
  useSearchRoleQuery,
  useSearchSiteQuery,
  useSearchSQDCTemplateQuery,
  ChartTypes,
  useSearchDatapointTypeQuery,
  useSearchQAChecklistQuery,
  useSearchDFTemplateQuery,
  useHLPCategoriesQuery,
  useQAProductFamiliesQuery,
  useQAErrorCataloguesQuery,
  useSQDCBoardsForSelectQuery,
  useSQDCTilesForBoardSelectQuery,
  useSearchShiftScheduleTemplatesQuery,
  useSearchBDEDataSourcesQuery,
  useSearchBDEOperatorsQuery,
} from "@/graphql";
import { defineSelectMode } from "./SelectHelper";
import store from "@/store";
import { computed, defineAsyncComponent, markRaw, toValue } from "vue";
import { getTileIcon } from "@/modules/tiles/tileConfigs";
import { getSQDCTileName } from "@/modules/tiles/helper/SQDCTileNameHelper";

function useSQDCTilesForBoardSearchSelectQuery(
  variables: Parameters<typeof useSQDCTilesForBoardSelectQuery>[0],
) {
  return useSQDCTilesForBoardSelectQuery(
    variables,
    computed(() => ({ enabled: toValue(variables).boardId > 0, fetchPolicy: "no-cache" })),
  );
}

export enum SelectMode {
  USER = "USER",
  ROLE = "ROLE",
  DATAPOINT_TYPE = "DATAPOINT_TYPE",
  SQDC_TEMPLATE = "SQDC_TEMPLATE",
  SITE = "SITE",
  FLAG = "FLAG",
  WORKFLOW = "WORKFLOW",
  IHEQUIPMENT = "IHEQUIPMENT",
  CHARTLAYOUT = "CHARTLAYOUT",
  QACHECKLIST = "QACHECKLIST",
  DFTEMPLATE = "DFTEMPLATE",
  IDEA_CATEGORY = "IDEA_CATEGORY",
  QA_PRODUCT_FAMILY = "QA_PRODUCT_FAMILY",
  QA_ERROR_CATALOGUE = "QA_ERROR_CATALOGUE",
  SQDC_BOARD = "SQDC_BOARD",
  SQDC_TILE = "SQDC_TILE",
  SHIFT_SCHEDULE_TEMPLATE = "SHIFT_SCHEDULE_TEMPLATE",
  BDE_DATA_SOURCE = "BDE_DATA_SOURCE",
  BDE_OPERATOR = "BDE_OPERATOR",
}

function addNamePosix(element: { id?: number }) {
  if (element.id == null) {
    return ``;
  }
  return ` [ID: ${element.id}]`;
}

export function mapUserToSelectOption(user: { id: number; preferredName: string }) {
  return {
    id: user.id,
    label: `${user.preferredName}`, //${addNamePosix(user)}`,
  };
}

export const SelectModeMap = {
  [SelectMode.BDE_OPERATOR]: defineSelectMode({
    useQuery: useSearchBDEOperatorsQuery,
    mapResult: (result) =>
      result.value?.BDEOperators.map((operator) => ({
        id: operator.id,
        label: `${operator.user?.preferredName ?? ""} [ID: ${operator.userId}]`,
      })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? [],
  }),
  [SelectMode.BDE_DATA_SOURCE]: defineSelectMode({
    useQuery: useSearchBDEDataSourcesQuery,
    mapResult: (result) =>
      result.value?.BDEDataSources.map((dataSource) => ({
        id: dataSource.id,
        label: `${dataSource.name}${addNamePosix(dataSource)}`,
      })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? [],
  }),
  [SelectMode.SHIFT_SCHEDULE_TEMPLATE]: defineSelectMode({
    useQuery: useSearchShiftScheduleTemplatesQuery,
    mapResult: (result) =>
      result.value?.ShiftScheduleTemplates.map((template) => ({
        id: template.id,
        label: `${template.title}${addNamePosix(template)}`,
      })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? [],
    linkToOrigin: {
      name: "ShiftScheduleTemplate",
    },
  }),
  [SelectMode.SQDC_BOARD]: defineSelectMode({
    useQuery: useSQDCBoardsForSelectQuery,
    mapResult: (result) =>
      result.value?.SQDCBoards.map((board) => ({
        id: board.id,
        label: `${board.name}${addNamePosix(board)}`,
        icon: "fab fa-flipboard",
      })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? [],
  }),
  [SelectMode.SQDC_TILE]: defineSelectMode({
    useQuery: useSQDCTilesForBoardSearchSelectQuery,
    mapResult: (result, t) => {
      const occurrenceCounts = new Map<number, number>();
      for (const layout of result.value?.occurrenceBoards.flatMap((board) => board.layouts) ?? []) {
        if (layout.tile != null) {
          occurrenceCounts.set(layout.tile.id, (occurrenceCounts.get(layout.tile.id) ?? 0) + 1);
        }
      }
      const tiles = new Map(
        result.value?.sourceBoards
          .flatMap((board) => board.layouts)
          .map((layout) => layout.tile)
          .filter((tile) => tile != null)
          .map((tile) => [tile.id, tile] as const) ?? [],
      );
      return [...tiles.values()]
        .map((tile) => ({
          id: tile.id,
          label: `${getSQDCTileName(tile, t)}${addNamePosix(tile)} (${occurrenceCounts.get(tile.id) ?? 0}×)`,
          icon: getTileIcon(tile.type),
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label));
    },
  }),
  [SelectMode.IDEA_CATEGORY]: defineSelectMode({
    useQuery: () => useHLPCategoriesQuery(),
    mapResult: (result) => {
      return (
        result.value?.HLPCategories.map((category) => ({
          id: category.id,
          label: `${category.name}${addNamePosix(category)}`,
        })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
    linkToOrigin: {
      name: "HLPCategory",
    },
  }),
  [SelectMode.IHEQUIPMENT]: defineSelectMode({
    useQuery: useSearchIHEquipmentQuery,
    mapResult: (result) => {
      return (
        result.value?.IHEquipments.toSorted((a, b) => a.name.localeCompare(b.name)).map((u) => ({
          id: u.id,
          label: `${u.name}${addNamePosix(u)}`,
        })) ?? []
      );
    },
    linkToOrigin: {
      name: "IHEquipments",
    },
  }),
  [SelectMode.WORKFLOW]: defineSelectMode({
    useQuery: useSearchWorkflowQuery,
    mapResult: (result) => {
      return (
        result.value?.Workflows.toSorted((a, b) => a.name.localeCompare(b.name)).map((u) => ({
          id: u.id,
          label: `${u.name}${addNamePosix(u)}`,
        })) ?? []
      );
    },
    linkToOrigin: {
      name: "WorkflowOverview",
    },
  }),
  [SelectMode.FLAG]: defineSelectMode({
    useQuery: useSearchFlagQuery,
    mapResult: (result, t, te) => {
      return (
        result.value?.Flags.map((flag) => {
          if (flag.displayNameId && te(`dynamic.${flag.displayNameId}`)) {
            return {
              id: flag.id,
              label: `${t(`dynamic.${flag.displayNameId}`)}${te(`dynamic.${flag.abbreviationId}`) ? ` (${t(`dynamic.${flag.abbreviationId}`)})` : ""}`, //${addNamePosix(flag)}`,
              color: flag.color ?? "#41b883",
            };
          }
          return {
            id: flag.id,
            label: `${flag.name}${addNamePosix(flag)}`,
            color: flag.color ?? "#41b883",
          };
        }).toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
    linkToOrigin: {
      name: "UpdateFlag",
    },
    createComponent: markRaw(
      defineAsyncComponent(() => import("@/modules/flags/components/CreateFlagInline.vue")),
    ),
  }),
  [SelectMode.SITE]: defineSelectMode({
    useQuery: useSearchSiteQuery,
    mapResult: (result) => {
      return (
        result.value?.Sites.map((u) => ({
          id: u.id,
          label: `${u.name}${addNamePosix(u)}`,
        })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
    linkToOrigin: {
      name: "SiteUpdate",
    },
  }),
  [SelectMode.USER]: defineSelectMode({
    useQuery: useSearchUsersQuery,
    mapResult: (result) => {
      const ownUserId = store.getters["login/userId"];
      return (
        result.value?.Users.map(mapUserToSelectOption).toSorted((a, b) =>
          a.id === ownUserId ? -1 : b.id === ownUserId ? 1 : a.label.localeCompare(b.label),
        ) ?? []
      );
    },
    linkToOrigin: {
      name: "UpdateUser",
    },
    specialFilters: (filters) => {
      return { hide: store.getters["login/userId"] === 1 ? undefined : false, ...filters };
    },
  }),
  [SelectMode.ROLE]: defineSelectMode({
    useQuery: useSearchRoleQuery,
    mapResult: (result) => {
      const assignedRoleIds = new Set(
        result.value?.RoleSiteUsers.filter(
          ({ userId }) => userId === store.getters["login/userId"],
        ).map(({ roleId }) => roleId) ?? [],
      );
      return (
        result.value?.Roles.filter((role) => !role.admin || assignedRoleIds.has(role.id))
          .map((u) => ({
            id: u.id,
            label: `${u.name}${addNamePosix(u)}`,
          }))
          .toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
    linkToOrigin: {
      name: "role",
    },
  }),
  [SelectMode.SQDC_TEMPLATE]: defineSelectMode({
    useQuery: useSearchSQDCTemplateQuery,
    mapResult: (result, t) => {
      return (
        result.value?.SQDCTemplates.map((u) => ({
          id: u.id,
          label: `${u.name} (${u.columns.length} ${t("components.shared.searchSelects.SelectDefinitions.column", { count: u.columns.length })}) ${addNamePosix(u)}`,
        })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
    linkToOrigin: {
      name: "boardTemplate",
    },
  }),

  [SelectMode.CHARTLAYOUT]: defineSelectMode({
    useQuery: useSearchChartLayoutQuery,
    mapResult: (result) => {
      return (
        result.value?.ChartLayouts.map((u) => ({
          id: u.id,
          label: `${u.name}${addNamePosix(u)}`,
        })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
    linkToOrigin: {
      name: "updateChartLayout",
      params: { type: ChartTypes.custom },
    },
  }),
  [SelectMode.DATAPOINT_TYPE]: defineSelectMode({
    useQuery: useSearchDatapointTypeQuery,
    mapResult: (result) => {
      return (
        result.value?.DatapointTypes.map((u) => ({
          id: u.id,
          label: `${u.name}${addNamePosix(u)}`,
        })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
    linkToOrigin: {
      name: "datapointType",
    },
    createComponent: markRaw(
      defineAsyncComponent(
        () => import("@/modules/datapointType/component/CreateDatapointType.vue"),
      ),
    ),
  }),
  [SelectMode.QACHECKLIST]: defineSelectMode({
    useQuery: useSearchQAChecklistQuery,
    mapResult: (result, t) => {
      return (
        result.value?.QAChecklists.map((u) => ({
          id: u.id,
          label: `${u.name} [${t("global.id")}: ${u.id}]`,
        })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
    linkToOrigin: {
      name: "qaChecklistUpdate",
    },
  }),
  [SelectMode.DFTEMPLATE]: defineSelectMode({
    useQuery: useSearchDFTemplateQuery,
    mapResult: (result) => {
      return (
        result.value?.DFTemplates.map((u) => ({
          id: u.id,
          label: `${u.name}${addNamePosix(u)}`,
        })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
    linkToOrigin: {
      name: "DFTemplateUpdate",
    },
  }),
  [SelectMode.QA_PRODUCT_FAMILY]: defineSelectMode({
    useQuery: useQAProductFamiliesQuery,
    mapResult: (result, t) => {
      return (
        result.value?.QAProductFamilies.map((pf) => ({
          id: pf.id,
          label: `${pf.code} – ${t(`dynamic.${pf.nameId}`)}`,
        })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
  }),
  [SelectMode.QA_ERROR_CATALOGUE]: defineSelectMode({
    useQuery: useQAErrorCataloguesQuery,
    mapResult: (result, t) => {
      return (
        result.value?.QAErrorCatalogues.map((ec) => ({
          id: ec.id,
          label: `${ec.code} – ${t(`dynamic.${ec.nameId}`)}`,
        })).toSorted((a, b) => a.label.localeCompare(b.label)) ?? []
      );
    },
  }),
};
